import { useMemo, useState } from "react";
import {
    ComposedChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
    CartesianGrid, Legend, ReferenceLine,
} from "recharts";

/**
 * Multi-stream Power Profile chart — plots PV, Consumption, Grid, and (for
 * hybrid systems) Battery Power + SOC across today's 5-min samples. Uses
 * two Y axes: left = kW, right = SOC %.
 */

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const SERIES = {
    pv: { label: "PV / Solar", color: "#0A0A0A", dataKey: "pv_kw" },
    consumption: { label: "Consumption", color: "#F59E0B", dataKey: "consumption_kw" },
    grid: { label: "Grid", color: "#2FA84F", dataKey: "grid_kw" },
    battery: { label: "Battery", color: "#3B82F6", dataKey: "battery_kw" },
    soc: { label: "SOC %", color: "#8B5CF6", dataKey: "battery_soc" },
};

export default function PowerProfileChart({ data, isHybrid }) {
    // Which series are visible — toggled via legend clicks
    const [hidden, setHidden] = useState({});

    const rows = useMemo(() => {
        return (data || []).map((p) => {
            const ts = typeof p.timestamp === "number" ? p.timestamp * 1000 : Date.parse(p.timestamp);
            const ist = new Date(ts + IST_OFFSET_MS + new Date().getTimezoneOffset() * 60 * 1000);
            const hh = String(ist.getUTCHours()).padStart(2, "0");
            const mm = String(ist.getUTCMinutes()).padStart(2, "0");
            return {
                time: `${hh}:${mm}`,
                pv_kw: p.pv_kw ?? 0,
                consumption_kw: p.consumption_kw ?? null,
                grid_kw: p.grid_kw ?? null,
                battery_kw: p.battery_kw != null ? p.battery_kw : null,
                battery_soc: p.battery_soc ?? null,
            };
        });
    }, [data]);

    const activeSeries = useMemo(() => {
        const base = ["pv", "consumption", "grid"];
        if (isHybrid) base.push("battery", "soc");
        return base.filter((k) => !hidden[k]);
    }, [isHybrid, hidden]);

    const ticks = useMemo(() => {
        const want = new Set(["00", "03", "06", "09", "12", "15", "18", "21"]);
        return rows.filter((r) => want.has(r.time.slice(0, 2)) && r.time.slice(3, 5) === "00").map((r) => r.time);
    }, [rows]);

    if (!rows.length) {
        return (
            <div className="h-72 flex items-center justify-center text-sm text-zinc-400" data-testid="power-profile-empty">
                No multi-stream telemetry available for this plant today.
            </div>
        );
    }

    return (
        <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid="power-profile-chart">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Power Profile</div>
                    <div className="text-sm text-zinc-700 mt-0.5">
                        Multi-stream · 5-min resolution · {rows.length} samples
                    </div>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="power-profile-legend">
                    {Object.entries(SERIES).filter(([k]) => k !== "soc" || isHybrid).filter(([k]) => k !== "battery" || isHybrid).map(([k, s]) => {
                        const off = !!hidden[k];
                        return (
                            <button
                                key={k}
                                onClick={() => setHidden((h) => ({ ...h, [k]: !h[k] }))}
                                className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-sm border transition-colors ${off ? "opacity-40" : ""}`}
                                style={{ borderColor: s.color + "55", background: off ? "transparent" : s.color + "10", color: s.color }}
                                data-testid={`toggle-series-${k}`}
                            >
                                <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                                {s.label}
                            </button>
                        );
                    })}
                </div>
            </div>
            <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={rows} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                        <XAxis dataKey="time" ticks={ticks} interval={0} tick={{ fontSize: 11, fill: "#71717A", fontFamily: "IBM Plex Mono" }} />
                        <YAxis yAxisId="kw" tick={{ fontSize: 11, fill: "#71717A", fontFamily: "IBM Plex Mono" }} tickFormatter={(v) => v.toFixed(1)} label={{ value: "kW", angle: -90, position: "insideLeft", offset: 15, style: { fontSize: 10, fill: "#71717A" } }} />
                        {isHybrid && (
                            <YAxis yAxisId="soc" orientation="right" domain={[0, 100]} tick={{ fontSize: 11, fill: "#71717A", fontFamily: "IBM Plex Mono" }} tickFormatter={(v) => `${v}%`} label={{ value: "SOC", angle: 90, position: "insideRight", offset: 15, style: { fontSize: 10, fill: "#71717A" } }} />
                        )}
                        <Tooltip
                            contentStyle={{ borderRadius: 4, border: "1px solid #E4E4E7", fontSize: 12, fontFamily: "Outfit" }}
                            labelStyle={{ color: "#6B7280", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em" }}
                            formatter={(value, name) => {
                                if (value == null) return [null, name];
                                const unit = name === "SOC %" ? "%" : "kW";
                                return [`${Number(value).toFixed(2)} ${unit}`, name];
                            }}
                        />
                        <ReferenceLine yAxisId="kw" y={0} stroke="#A1A1AA" strokeDasharray="2 2" />
                        {activeSeries.includes("pv") && (
                            <Line yAxisId="kw" type="monotone" dataKey="pv_kw" name={SERIES.pv.label} stroke={SERIES.pv.color} strokeWidth={2} dot={false} connectNulls />
                        )}
                        {activeSeries.includes("consumption") && (
                            <Line yAxisId="kw" type="monotone" dataKey="consumption_kw" name={SERIES.consumption.label} stroke={SERIES.consumption.color} strokeWidth={2} dot={false} connectNulls />
                        )}
                        {activeSeries.includes("grid") && (
                            <Line yAxisId="kw" type="monotone" dataKey="grid_kw" name={SERIES.grid.label} stroke={SERIES.grid.color} strokeWidth={2} dot={false} connectNulls />
                        )}
                        {isHybrid && activeSeries.includes("battery") && (
                            <Line yAxisId="kw" type="monotone" dataKey="battery_kw" name={SERIES.battery.label} stroke={SERIES.battery.color} strokeWidth={2} dot={false} connectNulls />
                        )}
                        {isHybrid && activeSeries.includes("soc") && (
                            <Line yAxisId="soc" type="monotone" dataKey="battery_soc" name={SERIES.soc.label} stroke={SERIES.soc.color} strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
